import Vue from 'vue';
import { get, capitalize, debounce } from 'lodash';
import utils, {
  findIndexInNestedObjectValues,
  groupByAndModify,
  LabelTaggingMode,
  SpecialLabels,
  HighlightStyle,
} from '@/utils';
import { AssetController, ImageController, MetricController, PartController } from '@/controllers';
import { CorrosionText } from '@/components';

let debouncedMarkerUpdates;
let updatedEquipment = [];

const addRenderIfCoverage = (unit) => (element) => ({
  ...element,
  ...(element.render === 'coverage' && {
    render: ({ metrics: { area } }) => CorrosionText({ name: element.friendlyName, area, unit }),
  }),
});

const initialState = {
  error: undefined,
  notification: undefined,
  loading: undefined,
  assets: {},
  layers: {},
  markers: [],
  checkedLayers: {
    defects: [],
    assets: [],
    lines: [],
  },
  selectedEquipmentId: undefined,
  selectedAsset: undefined,
  defectLayers: [],
  assetLayers: [],
  selectedPolygon: {
    id: undefined,
    annotationId: undefined,
    lineAssetIds: new Set(),
  },
  corrosionData: undefined,
  defectAndAssetLayersWithRenderers: undefined,
  neighbouringImages: [],
};

const getters = {
  error: (state) => state.error,
  notification: (state) => state.notification,
  loading: (state) => state.loading,
  markers: (state) => state.markers,
  layers: (state) => state.layers,
  checkedLayers: (state) => state.checkedLayers,
  assets: (state) => state.assets,
  selectedEquipmentId: (state) => state.selectedEquipmentId,
  selectedAsset: (state) => state.selectedAsset,
  defectLayers: (state) => state.defectLayers,
  neighbouringImages: (state) => state.neighbouringImages,
  assetLayers: (state, _, __, rootGetters) => {
    const sortValue = (key) => {
      switch (key) {
        case 'delete':
          return 2; // last
        case 'unlabelled':
          return rootGetters['config/labelTaggingMode'] === LabelTaggingMode.NONE ? 1 : -1; // top / 2nd to last
        default:
          return 0; // default order
      }
    };

    return [...state.assetLayers].sort((a, b) => sortValue(a.name) - sortValue(b.name));
  },
  corrosionData: (state) => state.corrosionData,
  selectedPolygon: (state) => state.selectedPolygon,
};

const actions = {
  async loadAssetsAndLayers({ commit, dispatch, rootGetters }, imageId) {
    const [{ data: parents }, { data: imageAssets }] = await Promise.all([
      MetricController.getMetricParents(imageId),
      ImageController.getImageAssets(imageId),
    ]);

    if (!parents) {
      commit('setError', 'Unable to get metric parents');
    } else {
      const clientDocument = parents.find(({ type }) => type === 'client');
      const inspectionDocument = parents.find(({ type }) => type === 'inspection');
      dispatch('inspectionDocument/setCurrentInspectionDocument', inspectionDocument, { root: true });

      // currently equipment layers are defined in the client locale
      const { defectLayerConfig, corrosionLayers } = rootGetters['config/inspectionConfig'];
      const assetLayers = clientDocument.data.locale.spherical.filter(({ category }) => category === 'Equipment');
      const defectLayers = defectLayerConfig.filter(({ name }) => get(corrosionLayers, [name, 'sphericalViewDisplay']));

      commit('setAssetLayers', assetLayers);
      commit('setDefectLayers', defectLayers);
      dispatch('updateDefectAndAssetLayers');
    }

    if (!imageAssets) {
      commit('setError', 'Unable to get image assets');
    } else {
      // key image assets by id
      const assets = imageAssets.reduce((images, image) => {
        const { _id: key } = image;
        // eslint-disable-next-line no-param-reassign
        images[key] = image;
        return images;
      }, {});

      commit('loadAssets', assets);
    }
  },
  async reloadAssets({ commit }, imageId) {
    commit('loadAssets', {}); // Clear assets
    const { data } = await ImageController.getImageAssets(imageId);
    if (!data) {
      commit('setError', 'Unable to get image assets');
    } else {
      // key image assets by id
      const assets = data.reduce((images, image) => {
        const { _id: key } = image;
        // eslint-disable-next-line no-param-reassign
        images[key] = image;
        return images;
      }, {});

      commit('loadAssets', assets);
    }
  },
  updateLayers({ commit, state, rootGetters }) {
    const isLabelTaggingMode = rootGetters['config/labelTaggingMode'] !== LabelTaggingMode.NONE;
    const defectAndAssetLayers = state.defectLayers.concat(state.assetLayers);
    const {
      type,
      data: { coverage, assets },
    } = rootGetters['inspectionDocument/imageDocument'];

    // initialise layers to set, with empty data arrays
    let setLayers = defectAndAssetLayers.reduce((layers, { name, color }) => {
      // eslint-disable-next-line no-param-reassign
      layers[name] = { color, data: [] };
      return layers;
    }, {});

    if (type === 'image') {
      const assetValues = Object.values(state.assets);

      // add corrosion equipment_id from image assets
      const groupCoverageLayersModifier = (data) => {
        const equipmentId = assetValues.find(({ data: { asset_id: assetId } }) => assetId === data.asset_id)?._id;
        return { ...data, equipment_id: equipmentId };
      };

      // Set data where the layer is not in defectAndAssetLayers to false so that it can be filtered out
      const groupedAssetEquipmentIdModifier = (data) => {
        const layer = get(state, ['assets', data.equipment_id, 'data', 'asset_class']) || data.type; // may be empty
        return (isLabelTaggingMode || defectAndAssetLayers.find(({ name }) => name === layer)) && data;
      };

      // NOTE: MUTATION
      // Add a new assetLayer if it does not already exist and add the data array to the layer (by reference)
      const addDataToLayersReference = (layersReference, layerName) => {
        if (!layersReference[layerName]) {
          const color = 'rgba(128, 50, 75, 0.5)';
          commit('appendToAssetLayers', {
            category: 'Equipment',
            color,
            friendlyName: capitalize(layerName),
            name: layerName,
          });
          // eslint-disable-next-line no-param-reassign
          layersReference[layerName] = { color, data: [] };
        }
      };

      const groupedCoverageLayers = groupByAndModify(coverage, 'type', groupCoverageLayersModifier);
      // equipment_id used to get the layer type from assets
      const groupedAssetEquipmentIds = groupByAndModify(assets, 'equipment_id', groupedAssetEquipmentIdModifier);

      setLayers = Object.entries(groupedCoverageLayers).reduce((layers, [key, data]) => {
        // eslint-disable-next-line no-param-reassign
        layers[key].data = data;
        return layers;
      }, setLayers);

      setLayers = Object.entries(groupedAssetEquipmentIds).reduce((layers, [key, data]) => {
        const layer = get(state, ['assets', key, 'data', 'asset_class']);
        if (layer && (isLabelTaggingMode || layer !== 'delete')) {
          addDataToLayersReference(layers, layer); // NOTE: Mutates layers by adding .[layer] if it does not exist
          // eslint-disable-next-line no-param-reassign
          layers[layer].data.push(...data.filter(Boolean));
        } else {
          console.log(`equipment_id: ${key} not mapped to an equipment layer`);
        }
        return layers;
      }, setLayers);
    }

    commit('setLayers', setLayers);
  },
  setMarkers: ({ commit, state, rootGetters }) => {
    const assetFillEnabled = rootGetters['config/assetFillEnabled'];
    const strokeWidth = `${rootGetters['config/strokeWidth']}px`;

    // Check which highlighting mode is being used, which defines the colouring schema
    const isHighlightingPart = rootGetters['config/highlightingStyle'] === HighlightStyle.PART;
    const isHighlightingAssembly = rootGetters['config/highlightingStyle'] === HighlightStyle.ASSEMBLY;

    const isLabelCorrosion = ({ type }) => state.defectLayers.some(({ name }) => name === type);

    const isLabelUnlabelled = ({ equipment_id }) =>
      get(state.assets, `${equipment_id}.name`, '') === SpecialLabels.unlabelled;

    const isLabelDelete = ({ equipment_id }) => get(state.assets, `${equipment_id}.name`, '') === SpecialLabels.deleted;

    const doesAnnotationIdMatch = ({ annotation_id = Symbol('Unmatched') }) =>
      state.selectedPolygon.annotationId === annotation_id;

    const isSamePart = ({ equipment_id }) => equipment_id === state.selectedEquipmentId;

    const isSameLine = ({ equipment_id }) =>
      get(state.assets, `${equipment_id}.name`, '') ===
      get(state.assets, `${state.selectedPolygon.equipmentId}.name`, '');

    const selectColorByEquipmentId = ({ equipment_id }) => {
      // todo: revert the function
      const equipmentIdToInteger = parseInt(equipment_id.substr(0, 7) + equipment_id.substr(18), 16);
      const { color, chromaColor } = utils.colorsBasedOnNumber(equipmentIdToInteger);
      return { fill: assetFillEnabled ? chromaColor : 'rgba(0, 0, 0, 0)', stroke: color, strokeWidth };
    };

    const selectColorByAssembly = ({ equipment_id }) => {
      const LineKey = get(state.assets, `${equipment_id}.name`, '');
      if (LineKey === SpecialLabels.unlabelled) {
        return {
          fill: assetFillEnabled ? 'rgba(0, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0)',
          stroke: 'rgba(0, 255, 255, 0.6)',
        };
      }
      const lineKeyChecksum = utils.stringChecksum(LineKey);
      const { color, chromaColor } = utils.colorsBasedOnNumber(lineKeyChecksum);
      return { fill: assetFillEnabled ? chromaColor : 'rgba(0, 0, 0, 0)', stroke: color, strokeWidth };
    };

    const highlighterMatchers = (defaultColor, annotation) => [
      // corrosionHighlighter
      {
        matcher: isLabelCorrosion,
        color: { fill: defaultColor },
      },
      ...(isHighlightingPart || isHighlightingAssembly
        ? [
            {
              matcher: doesAnnotationIdMatch,
              color: { fill: 'rgba(0, 255, 0, 0.25)' }, // green
            },
          ]
        : []),
      {
        matcher: isLabelUnlabelled,
        color: {
          fill: assetFillEnabled ? 'rgba(0, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0)',
          stroke: 'rgba(0, 255, 255, 0.6)',
        },
      },
      {
        matcher: isLabelDelete,
        color: { fill: assetFillEnabled ? 'rgba(0, 0, 0, 1)' : 'rgba(0, 0, 0, 0)', stroke: 'rgba(0, 0, 0, 1)' },
      },
      // equipmentHighlighter (same part)
      {
        matcher: isSamePart,
        color: { fill: 'rgba(255, 10, 197, 0.5)' }, // magenta
      },
      // equipmentHighlighter (same line)
      {
        matcher: isSameLine,
        color: { fill: 'rgba(255, 10, 197, 0.25)' }, // magenta
      },
      ...(isHighlightingPart
        ? [
            {
              matcher: () => true,
              color: selectColorByEquipmentId(annotation),
            },
          ]
        : []),
      ...(isHighlightingAssembly
        ? [
            {
              matcher: () => true,
              color: selectColorByAssembly(annotation),
            },
          ]
        : []),
      {
        matcher: () => true,
        color: { fill: assetFillEnabled ? defaultColor : 'rgba(0, 0, 0, 0)', stroke: defaultColor }, // transparent
      },
    ];

    const highlighter = (defaultColor) => (annotation) => {
      const matchers = highlighterMatchers(defaultColor, annotation);
      let index = 0;

      while (!matchers[index].matcher(annotation)) {
        index += 1;
      }
      return matchers[index].color;
    };

    const processDataIntoMarkers = (layer) => {
      const { data, color } = state.layers[layer];
      const fillColor = highlighter(color);
      return data.map((annotation) => {
        const { fill, stroke = fill } = fillColor(annotation);
        const polygon = {
          id: `polygon_${layer}_${annotation.id}`,
          polygonRad: annotation.polar.points[0].map(([pt1, pt2]) => [pt1, -pt2]),
          style: {
            cursor: 'pointer',
          },
          svgStyle: {
            fill,
            stroke,
            strokeWidth,
          },
          equipmentId: annotation.equipment_id, // add annotation id to polygon marker
          polygonData: annotation,
        };
        const tooltipContent = state.defectAndAssetLayersWithRenderers[layer].render;
        if (typeof tooltipContent === 'function') {
          polygon.tooltip = {
            content: tooltipContent(annotation),
            position: 'right',
          };
        }

        return polygon;
      });
    };

    const checkedDefectAndAssetLayers = state.checkedLayers.assets.concat(state.checkedLayers.defects);
    const markers = checkedDefectAndAssetLayers
      // filter out unchecked/empty layers
      .filter((checkedLayer) => state.layers[checkedLayer] && state.defectAndAssetLayersWithRenderers[checkedLayer])
      .flatMap(processDataIntoMarkers);

    commit('setMarkers', markers);
  },
  updateDefectAndAssetLayers: ({ commit, state, rootGetters }) => {
    const defectAndAssetLayers = state.defectLayers
      .map(addRenderIfCoverage(rootGetters.numericUnit))
      .concat(state.assetLayers)
      .reduce((layers, layer) => {
        // eslint-disable-next-line no-param-reassign
        layers[layer.name] = layer;
        return layers;
      }, {});

    commit('setDefectAndAssetLayersWithRenderers', defectAndAssetLayers);
  },
  setSelectedAssetAndEquipmentId({ commit }, id) {
    commit('setSelectedAssetAndEquipmentId', id);
  },
  clearSelectedPoly({ commit }) {
    commit('setSelectedPolygon', {
      id: undefined,
      annotationId: undefined,
      lineAssetIds: new Set(),
    });
  },
  async addSelectedAsset({ state, commit }, id) {
    if (!state.assets[id]) {
      const { data } = await AssetController.getAsset(id);
      if (!data) {
        commit('setError', 'Failed to get asset information');
      } else {
        commit('addAsset', { id, data });
      }
    }
  },
  async updateSelectedAnnotation({ commit, dispatch, state }, { platformId, equipmentId, imageId, annotationId }) {
    const fromEquipmentId = state.selectedEquipmentId;
    dispatch('updateAnnotationTag', { annotationId, fromEquipmentId, toEquipmentId: equipmentId });
    dispatch('updateSelectedPolygonLineAssetIds', equipmentId);
    commit('setSelectedAssetAndEquipmentId', equipmentId);

    const { error } = await ImageController.updateImagePolygon({
      platformId,
      assetId: equipmentId,
      imageId,
      annotationId,
    });
    if (error) {
      commit('setError', error);
      // revert optimistic updates
      dispatch('updateAnnotationTag', {
        annotationId,
        fromEquipmentId: equipmentId,
        toEquipmentId: fromEquipmentId,
      });
      dispatch('updateSelectedPolygonLineAssetIds', fromEquipmentId);
      commit('setSelectedAssetAndEquipmentId', fromEquipmentId);
    } else {
      dispatch('inspectionDocument/updateImageDocument', [{ annotationId, equipmentId }], { root: true });
      commit('setNotification', 'Successfully updated equipment tag');
    }
  },
  async createPartInstance({ commit, dispatch, state }, { platformId, imageId, annotationId }) {
    const { error, data } = await PartController.createPartInstance({
      platformId,
      imageId,
      annotationId,
      fields: ['_id'],
    });
    if (data) {
      const toEquipmentId = data._id;
      commit('updateAssetsEquipmentId', { from: state.selectedEquipmentId, to: toEquipmentId });
      commit('setSelectedAssetAndEquipmentId', toEquipmentId);
      dispatch('setNotification', 'New part added');
    } else {
      dispatch('setError', `Unable to add new part: ${error}`);
    }
  },
  updateCheckedLayers({ commit, dispatch }, { layer, layerStatus }) {
    commit('setCheckedLayer', { layer, layerStatus });
    dispatch('setMarkers');
  },
  async setSelectedMarker({ state, dispatch }, { markerId, ctrlKey }) {
    const selectedPolygon = state.markers.find(({ id }) => id === markerId);
    if (selectedPolygon) {
      if (ctrlKey) {
        dispatch('silientMarkerUpdate', get(selectedPolygon, 'polygonData.annotation_id'));
      } else {
        const [, layer, layerId] = markerId.split('_');
        dispatch('updateMarker', { selectedPolygon, layer, layerId });
      }
    }
  },
  updateMarker({ commit, dispatch, state, rootGetters }, { selectedPolygon, layer, layerId }) {
    if (state.assetLayers.some(({ name }) => name === layer)) {
      // equipment polygons
      const { equipment_id: equipmentId } = state.layers[layer].data.find(({ id }) => id.toString() === layerId);
      if (state.assets[equipmentId] || rootGetters['inspectionDocument/assetPartById'](equipmentId)) {
        commit('setCorrosionData', {});
        commit('setSelectedAssetAndEquipmentId', equipmentId);
      }
    } else if (state.defectLayers.some(({ name }) => name === layer)) {
      // corrosion polygon
      const corrosionData = state.layers[layer].data.find(({ id }) => id.toString() === layerId);
      commit('setCorrosionData', corrosionData);
      // asset equipment with corrosion
      const corrosionAsset = Object.values(state.assets).find(
        ({ data: { asset_id: assetId } }) => assetId === corrosionData.asset_id
      );
      if (corrosionAsset) {
        // select equipment with corrosion
        commit('setSelectedAssetAndEquipmentId', corrosionAsset._id);
      }
    }

    commit('setSelectedPolygon', {
      equipmentId: selectedPolygon.equipmentId,
      id: selectedPolygon.id,
      annotationId: selectedPolygon.polygonData.annotation_id,
      data: selectedPolygon.polygonData,
    });
    dispatch('updateSelectedPolygonLineAssetIds', selectedPolygon.equipmentId);
    dispatch('setMarkers');
  },
  async silientMarkerUpdate({ commit, dispatch, rootGetters, rootState }, annotationId) {
    const recentSelectedTags = rootGetters['labelTagging/recentSelectedTags'](LabelTaggingMode.ANNOTATION);
    const equipmentId = recentSelectedTags?.[0]?.tagId;
    const platformId = get(rootState, 'inspectionDocument.currentInspectionInfo._id');
    const imageId = get(rootState, 'inspectionDocument.imageDocument._id');

    if (platformId && equipmentId && imageId && annotationId) {
      await dispatch('addSelectedAsset', equipmentId);
      await dispatch('inspectionDocument/updateCurrentAssetParts', equipmentId, { root: true });

      const { error } = await ImageController.updateImagePolygon({
        platformId,
        assetId: equipmentId,
        imageId,
        annotationId,
      });
      if (!error) {
        updatedEquipment.push({ annotationId, equipmentId });
        if (!debouncedMarkerUpdates) {
          debouncedMarkerUpdates = debounce(() => {
            dispatch('inspectionDocument/updateImageDocument', updatedEquipment, { root: true });
            updatedEquipment = [];
            dispatch('updateLayers');
            dispatch('setMarkers');
            commit('setNotification', 'Successfully updated equipment tag(s)');
          }, 1500);
        }
        debouncedMarkerUpdates();
      }
    }
  },
  updateAnnotationTag({ commit, state }, { annotationId, fromEquipmentId, toEquipmentId }) {
    const getLayers = (id) => {
      const layerName = get(state, ['assets', id, 'data', 'asset_class']);
      return get(state, ['layers', layerName, 'data'], []);
    };

    let fromLayers = getLayers(fromEquipmentId);
    let index = fromLayers.findIndex(({ annotation_id: id }) => id === annotationId);
    // may need to search other layers as the selectedPolygon equipmentId is not updated unless a new layer is clicked
    if (index < 0) {
      ({
        index,
        value: { data: fromLayers },
      } = findIndexInNestedObjectValues(state.layers, 'data', ({ annotation_id: id }) => id === annotationId));
    }

    if (index >= 0) {
      const toLayers = getLayers(toEquipmentId);
      commit('updateAnnotationTag', { fromLayers, toLayers, index, equipmentId: toEquipmentId });
    } else {
      commit('setError', 'Unable to update annotation tag');
    }
  },
  updateSelectedPolygonLineAssetIds({ commit, state }, equipmentId) {
    const lineKey = get(state, ['assets', equipmentId, 'data', 'meta', 'AutoCad:LineKey']);
    const lineAssetIds = new Set(
      Object.values(state.assets)
        .filter((asset) => get(asset, ['data', 'meta', 'AutoCad:LineKey'], '') === lineKey)
        .map(({ _id: id }) => id)
    );
    commit('updateSelectedPolygonLineAssetIds', lineAssetIds);
  },
  updateAssetsWithAssemblies({ commit, dispatch }, assemblies) {
    commit('updateAssetsWithAssemblies', assemblies);

    // update selected polygon line with the last equipmentId in assemblies
    const [{ equipmentId }] = assemblies.slice(-1);
    dispatch('updateSelectedPolygonLineAssetIds', equipmentId);
    dispatch('setMarkers');
  },
  setNotification({ commit }, message) {
    commit('setNotification', message);
  },
  setError({ commit }, message) {
    commit('setError', message);
  },
  clearNotification({ commit }) {
    commit('clearNotification');
  },
  clearError({ commit }) {
    commit('clearError');
  },
  async getNeighbouringImages({ commit }, imageId) {
    const { data } = await ImageController.getNeighbouringImages(imageId);
    if (!data) {
      commit('setError', 'Failed to get asset information');
    } else {
      commit('addNeighbouringImages', data);
    }
  },
};

const mutations = {
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, message) {
    state.error = message;
  },
  setNotification(state, message) {
    state.notification = message;
  },
  clearError(state) {
    state.error = undefined;
  },
  clearNotification(state) {
    state.notification = undefined;
  },
  setMarkers(state, markers) {
    Vue.set(state, 'markers', markers);
  },
  setCheckedLayer(state, { layer, layerStatus }) {
    Vue.set(state.checkedLayers, layer, layerStatus);
  },
  setLayers(state, layers) {
    Vue.set(state, 'layers', layers);
  },
  setCorrosionData(state, data) {
    Vue.set(state, 'corrosionData', data);
  },
  setSelectedAssetAndEquipmentId(state, id) {
    state.selectedEquipmentId = id;
    state.selectedAsset = state.assets[id];
  },
  loadAssets(state, assets) {
    Vue.set(state, 'assets', assets);
  },
  setAssetLayers(state, layers) {
    Vue.set(state, 'assetLayers', layers);
  },
  setDefectLayers(state, layers) {
    Vue.set(state, 'defectLayers', layers);
  },
  setDefectAndAssetLayersWithRenderers(state, layers) {
    Vue.set(state, 'defectAndAssetLayersWithRenderers', layers);
  },
  appendToAssetLayers(state, layers) {
    state.assetLayers.push(layers);
  },
  setSelectedPolygon(state, polygon) {
    Vue.set(state, 'selectedPolygon', polygon);
  },
  updateSelectedPolygonLineAssetIds(state, lineAssetIds) {
    state.selectedPolygon.lineAssetIds = lineAssetIds;
  },
  updateAnnotationTag(_, { fromLayers, toLayers, index, equipmentId }) {
    // Move data between the layers
    toLayers.push({ ...fromLayers[index], equipment_id: equipmentId });
    Vue.delete(fromLayers, index);
  },
  addAsset(state, { id, data }) {
    Vue.set(state.assets, id, data);
  },
  updateAssetsWithAssemblies(state, assemblies) {
    assemblies.forEach(({ equipmentId, assemblyName }) => {
      state.assets[equipmentId].data.meta['AutoCad:LineKey'] = assemblyName;
      state.assets[equipmentId].name = assemblyName;
    });
  },
  updateAssetsEquipmentId(state, { from, to }) {
    Object.defineProperty(state.assets, to, Object.getOwnPropertyDescriptor(state.assets, from));
    Vue.delete(state.assets, from);
  },
  addNeighbouringImages(state, images) {
    Vue.set(state, 'neighbouringImages', images);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
